import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus, ArrowRight } from "lucide-react";
import {
  getDashboard,
  getSystemHealth,
  getActivity,
} from "../../../api/dashboard";
import SampleDataBanner from "../../../components/onboarding/SampleDataBanner";
import SetupChecklist from "../../../components/onboarding/SetupChecklist";

/* ============================================================================
 * DESIGN TOKENS — aligned to amplehire-frontend-v2 (Home/Command Center)
 * ============================================================================ */
const C = {
  ink: "#1A2332",
  sub: "#64748B",
  hush: "#94A3B8",
  border: "rgba(0,0,0,0.08)",
  hairline: "rgba(0,0,0,0.05)",
  card: "#FFFFFF",
  surface: "#F0F2F5",
  tint: "#F2EEF9",
  primary: "#7B5AC4",
  teal: "#1F8A7D",
  magenta: "#B865A8",
  green: "#16A34A",
  sky: "#2E7DAF",
  amber: "#D4572D",
  gold: "#B8942F",
  red: "#DC2626",
};

// Glass panel — mirrors SOURCE .card-glass. Used via className + inline padding.
const glassPad = (p = "16px") => ({ padding: p });

const cardHead = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 14,
  gap: 8,
};

// Uppercase muted panel title — SOURCE Home h3 treatment.
const h3 = {
  fontSize: 11,
  fontWeight: 600,
  color: C.sub,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  margin: 0,
};

const linkBtn = {
  fontSize: 11,
  color: C.primary,
  background: "none",
  border: "none",
  cursor: "pointer",
  fontWeight: 600,
  padding: 0,
};

// Period toggle: UI label → API `period` value (today | 7d | 30d | all).
const PERIOD_OPTS = [
  { label: "Today", value: "today" },
  { label: "7d", value: "7d" },
  { label: "30d", value: "30d" },
  { label: "All time", value: "all" },
];

// KPI key → accent colour (SOURCE module palette), with a violet fallback.
const KPI_COLORS = {
  openJobs: C.primary,
  activeCandidates: C.teal,
  interviewsWeek: C.magenta,
  interviewsThisWeek: C.magenta,
  avgAiScore: C.sky,
  offerRate: C.green,
  timeToHire: C.sky,
  rejectionRate: C.amber,
};
const kpiColor = (key) => KPI_COLORS[key] || C.primary;

// Fallback KPI scaffold so the 4-tile grid keeps its shape when the kpis query
// degrades to [] (brand-new tenant / failed widget).
const DEFAULT_KPIS = [
  { key: "openJobs", label: "Open jobs", to: "/admin/jobs" },
  { key: "activeCandidates", label: "Active candidates", to: "/admin/talent-pools" },
  { key: "interviewsWeek", label: "Interviews / wk", to: "/admin/interviews" },
  { key: "avgAiScore", label: "Avg AI score", to: "/admin/candidate-report" },
];

const quarterLabel = () => {
  const d = new Date();
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
};

const isEmptyArr = (a) => !Array.isArray(a) || a.length === 0;

// Staggered entrance preset for panels.
const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.45, ease: [0.23, 1, 0.32, 1] },
});

/* ============================================================================
 * SHARED — animated counter, sparkline, empty state, skeletons
 * ============================================================================ */

// Eases a numeric value up on mount/update. Non-numeric values render as-is.
const AnimatedCounter = ({ value, duration = 1400 }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(0);
  const numeric = typeof value === "number" && Number.isFinite(value);
  useEffect(() => {
    if (!numeric) return;
    const start = ref.current;
    const diff = value - start;
    const t0 = performance.now();
    let raf;
    const animate = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const v = start + diff * (1 - Math.pow(1 - p, 3));
      setDisplay(v);
      ref.current = v;
      if (p < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, numeric]);
  if (!numeric) return <>{value}</>;
  if (Number.isInteger(value)) return <>{Math.round(display).toLocaleString("en-IN")}</>;
  return <>{display.toFixed(1)}</>;
};

const MiniSparkline = ({ data, color = C.primary }) => {
  if (!data || data.length < 2) return null;
  const w = 72;
  const h = 24;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = range === 0 ? h / 2 : h - ((v - min) / range) * (h - 4) - 2;
      return `${x},${y.toFixed(2)}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} style={{ opacity: 0.75, display: "block" }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// On-brand empty state used inside a card body.
const EmptyState = ({ icon, title, hint, cta, onCta, dense }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: dense ? "16px 12px" : "26px 16px",
      gap: 6,
    }}
  >
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: C.tint,
        border: `1px solid ${C.border}`,
        fontSize: 18,
      }}
    >
      {icon}
    </div>
    <div style={{ fontSize: 13, fontWeight: 700, color: C.sub }}>{title}</div>
    {hint && <div style={{ fontSize: 11, color: C.hush, maxWidth: 280, lineHeight: 1.5 }}>{hint}</div>}
    {cta && (
      <button
        onClick={onCta}
        style={{
          marginTop: 4,
          padding: "6px 14px",
          borderRadius: 999,
          border: `1px solid ${C.primary}33`,
          background: C.tint,
          color: C.primary,
          fontSize: 11,
          fontWeight: 700,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        {cta}
      </button>
    )}
  </div>
);

const Shimmer = ({ h = 14, w = "100%", r = 6, style }) => (
  <div className="dv2-shimmer" style={{ height: h, width: w, borderRadius: r, ...style }} />
);

const SkeletonCard = ({ lines = 4, height }) => (
  <div className="card-glass" style={{ ...glassPad(), height }}>
    <Shimmer h={12} w="40%" style={{ marginBottom: 14 }} />
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Shimmer key={i} h={12} w={`${90 - i * 8}%`} />
      ))}
    </div>
  </div>
);

/* ============================================================================
 * WIDGETS
 * ============================================================================ */

// §3 Action Strip — pill chips
const ActionStrip = ({ chips, navigate }) => {
  const list = Array.isArray(chips) ? chips : [];
  const allClear = list.length === 0 || list.every((c) => !c.count);

  if (allClear) {
    return (
      <motion.div
        {...reveal(0.05)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 16px",
          border: `1px solid ${C.green}33`,
          background: `${C.green}10`,
          color: C.green,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        <span style={{ fontSize: 14 }}>✓</span>
        All clear — nothing needs your attention right now.
      </motion.div>
    );
  }

  const toneStyle = (tone, isZero) => {
    if (isZero) return { color: C.hush, background: C.hairline, borderColor: C.border };
    if (tone === "danger") return { color: C.red, background: `${C.red}12`, borderColor: `${C.red}33` };
    if (tone === "alert") return { color: C.amber, background: `${C.amber}12`, borderColor: `${C.amber}33` };
    if (tone === "warn") return { color: C.gold, background: `${C.gold}12`, borderColor: `${C.gold}33` };
    return { color: C.sky, background: `${C.sky}12`, borderColor: `${C.sky}33` };
  };
  return (
    <motion.div {...reveal(0.05)} style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
      {list.map((c, i) => {
        const isZero = !c.count;
        const ts = toneStyle(c.tone, isZero);
        return (
          <motion.button
            key={c.key || i}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => c.to && navigate(c.to)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              border: `1px solid ${ts.borderColor}`,
              background: ts.background,
              color: ts.color,
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
              fontFamily: "inherit",
            }}
          >
            <span style={{ fontSize: 13 }}>{c.icon}</span>
            <span className="font-kpi" style={{ fontWeight: 800, fontSize: 13 }}>{c.count}</span>
            <span style={{ fontWeight: 500 }}>{c.label}</span>
          </motion.button>
        );
      })}
    </motion.div>
  );
};

// §4 KPI Tile — glass card with animated counter, sparkline, trend badge, pulsing dot
const KpiTile = ({ tile, navigate, index }) => {
  const color = kpiColor(tile.key);
  const hasValue = tile.value !== null && tile.value !== undefined && tile.value !== "";
  const val = hasValue ? tile.value : "—";
  const hasTrend = Array.isArray(tile.spark) && tile.spark.length >= 2;
  const hasDelta = typeof tile.delta === "number" && tile.delta !== 0;
  const positive = (tile.delta ?? 0) >= 0;
  const TrendIcon = !hasDelta ? Minus : positive ? TrendingUp : TrendingDown;
  const trendColor = !hasDelta ? C.hush : positive ? C.green : C.red;
  return (
    <motion.button
      initial={{ opacity: 0, y: 24, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: index * 0.08, duration: 0.5, type: "spring", stiffness: 260, damping: 20 }}
      whileHover={{ y: -6, scale: 1.03, boxShadow: `0 20px 40px ${color}18, 0 0 0 1px ${color}15` }}
      whileTap={{ scale: 0.98 }}
      onClick={() => tile.to && navigate(tile.to)}
      style={{
        borderRadius: 16,
        padding: "18px 20px",
        textAlign: "left",
        cursor: tile.to ? "pointer" : "default",
        position: "relative",
        overflow: "hidden",
        background: "rgba(255,255,255,0.7)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.8)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.04), 0 1px 3px rgba(0,0,0,0.02)",
        fontFamily: "inherit",
      }}
    >
      {/* top gradient accent */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, borderRadius: "16px 16px 0 0", background: `linear-gradient(90deg, ${color}, ${color}88)` }} />
      {/* corner glow */}
      <div style={{ position: "absolute", top: -20, right: -20, width: 80, height: 80, borderRadius: "50%", background: `radial-gradient(circle, ${color}10 0%, transparent 70%)`, filter: "blur(15px)", pointerEvents: "none" }} />

      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.hush, textTransform: "uppercase", letterSpacing: "0.06em" }}>{tile.label}</span>
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: index * 0.5 }}
            style={{ width: 36, height: 36, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: `linear-gradient(135deg, ${color}18, ${color}08)`, border: `1px solid ${color}20` }}
          >
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 10px ${color}50` }} />
          </motion.div>
        </div>

        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 8 }}>
          <div>
            <div className="font-kpi" style={{ fontSize: 28, fontWeight: 800, color: hasValue ? C.ink : C.hush, letterSpacing: "-0.02em", lineHeight: 1 }}>
              <AnimatedCounter value={val} />
              {hasValue && tile.suffix && <span style={{ fontSize: 14, color: C.hush, marginLeft: 4, fontWeight: 500 }}>{tile.suffix}</span>}
            </div>
            {hasDelta ? (
              <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 3, padding: "2px 7px", borderRadius: 6, background: `${trendColor}10`, border: `1px solid ${trendColor}20` }}>
                  <TrendIcon size={11} style={{ color: trendColor }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: trendColor }}>{positive ? "+" : ""}{tile.delta}{tile.deltaUnit || ""}</span>
                </div>
                {tile.deltaLabel && <span style={{ fontSize: 10, color: C.hush }}>{tile.deltaLabel}</span>}
              </div>
            ) : (
              <div style={{ fontSize: 10, color: C.hush, marginTop: 8 }}>{hasTrend ? "" : "No data yet"}</div>
            )}
          </div>
          {hasTrend && (
            <div style={{ flexShrink: 0 }}>
              <MiniSparkline data={tile.spark} color={color} />
            </div>
          )}
        </div>
      </div>
    </motion.button>
  );
};

// §5 Pipeline Funnel
const PipelineFunnel = ({ rows, navigate, delay }) => {
  const list = Array.isArray(rows) ? rows : [];
  const hasData = list.some((r) => r.value > 0);
  const max = Math.max(1, ...list.map((r) => r.value || 0));
  return (
    <motion.div {...reveal(delay)} className="card-glass" style={glassPad()}>
      <div style={cardHead}>
        <h3 style={h3}>Pipeline (all jobs)</h3>
        <button style={linkBtn} onClick={() => navigate("/admin/pipeline")}>View pipeline →</button>
      </div>
      {!hasData ? (
        <EmptyState
          icon="🫧"
          title="No pipeline activity yet"
          hint="As candidates are sourced, invited and scored, your funnel will appear here."
          cta="Source candidates"
          onCta={() => navigate("/admin/search-candidates")}
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {list.map((r, i) => {
            const pct = Math.max(4, Math.round(((r.value || 0) / max) * 100));
            return (
              <div key={r.stage}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12 }}>
                  <span style={{ width: 90, color: C.sub, fontWeight: 600 }}>{r.stage}</span>
                  <div style={{ flex: 1, background: C.surface, borderRadius: 4, height: 22, position: "relative", overflow: "hidden" }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ delay: (delay || 0) + 0.2 + i * 0.06, duration: 0.6, ease: "easeOut" }}
                      style={{ height: "100%", background: r.isBiggestDrop ? `linear-gradient(90deg, ${C.primary} 0%, ${C.red} 100%)` : C.primary, borderRadius: 4 }}
                    />
                  </div>
                  <span className="font-kpi" style={{ width: 36, textAlign: "right", color: C.ink, fontWeight: 700, fontSize: 12 }}>{r.value}</span>
                </div>
                {r.drop !== null && r.drop !== undefined && i < list.length - 1 && (
                  <div style={{ display: "flex", justifyContent: "center", margin: "1px 0" }}>
                    <span style={{ fontSize: 10, color: r.isBiggestDrop ? C.red : C.hush, fontWeight: r.isBiggestDrop ? 700 : 500 }}>
                      ↓ {r.drop}% drop {r.isBiggestDrop && "· biggest"}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

// §6 Hiring Goals
const HiringGoals = ({ goals, delay }) => {
  const list = Array.isArray(goals) ? goals : [];
  const flag = (s) => {
    if (s === "done") return { label: "✓ done", color: C.green };
    if (s === "behind") return { label: "⚠ behind", color: C.red };
    return { label: "on track", color: C.sky };
  };
  return (
    <motion.div {...reveal(delay)} className="card-glass" style={glassPad()}>
      <div style={cardHead}>
        <h3 style={h3}>Hiring goals ({quarterLabel()})</h3>
        <button style={linkBtn}>+ Goal</button>
      </div>
      {isEmptyArr(list) ? (
        <EmptyState icon="🎯" title="No hiring goals set" hint="Set targets per team to track progress against your quarter plan." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {list.map((g) => {
            const pct = g.target ? Math.min(100, Math.round((g.filled / g.target) * 100)) : 0;
            const f = flag(g.status);
            return (
              <div key={g.id || g.team}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, color: C.ink }}>{g.team} — {g.target} hires</span>
                  <span style={{ color: f.color, fontSize: 11, fontWeight: 600 }}>{f.label}</span>
                </div>
                <div style={{ background: C.surface, height: 8, borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: g.status === "behind" ? "#FCA5A5" : C.primary }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 11, color: C.sub }}>
                  <span className="font-kpi">{g.filled} / {g.target}</span>
                  {g.deadline && <span>by {g.deadline}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

// §7 Top Candidates
const TopCandidates = ({ rows, total, navigate, delay }) => {
  const list = Array.isArray(rows) ? rows : [];
  const scoreColor = (s) => (s >= 8 ? C.green : s >= 6 ? C.gold : C.sub);
  return (
    <motion.div {...reveal(delay)} className="card-glass" style={glassPad()}>
      <div style={cardHead}>
        <h3 style={h3}>Top candidates awaiting your review</h3>
        {!isEmptyArr(list) && <span style={{ fontSize: 11, color: C.hush }}>sort: score ▼</span>}
      </div>
      {isEmptyArr(list) ? (
        <EmptyState icon="🌟" title="No candidates awaiting review" hint="Scored candidates that need a decision will surface here, ranked by AI score." />
      ) : (
        <>
          <div>
            {list.map((c) => (
              <button
                key={c.id}
                onClick={() => navigate(`/admin/candidate-report?candidateId=${c.id}`)}
                style={{
                  display: "grid",
                  gridTemplateColumns: "44px 1fr auto auto 12px",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 0",
                  borderBottom: `1px solid ${C.hairline}`,
                  background: "none",
                  border: "none",
                  borderBottomColor: C.hairline,
                  borderBottomStyle: "solid",
                  borderBottomWidth: 1,
                  width: "100%",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                }}
              >
                <span className="font-kpi" style={{ color: scoreColor(c.score), fontWeight: 800, fontSize: 14 }}>★ {c.score}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.ink }}>{c.name}</div>
                  <div style={{ fontSize: 10, color: C.sub }}>{c.job} · {c.round}</div>
                </div>
                <span style={{ fontSize: 10, color: C.hush }}>{c.time}</span>
                <span />
                <span style={{ color: C.hush }}>→</span>
              </button>
            ))}
          </div>
          {total > 0 && (
            <button style={{ ...linkBtn, marginTop: 8 }} onClick={() => navigate("/admin/interviews?filter=awaiting")}>
              View all {total} →
            </button>
          )}
        </>
      )}
    </motion.div>
  );
};

// §8 Interview Throughput
const Legend = ({ color, label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
    <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
    {label}
  </span>
);

const InterviewThroughput = ({ data, backlogHours, delay }) => {
  const list = Array.isArray(data) ? data : [];
  const hasData = list.some((d) => d.started || d.completed || d.scored);
  const max = Math.max(1, ...list.map((d) => d.started || 0));
  return (
    <motion.div {...reveal(delay)} className="card-glass" style={glassPad()}>
      <div style={cardHead}>
        <h3 style={h3}>Interview throughput (14d)</h3>
        <div style={{ display: "flex", gap: 10, fontSize: 10, color: C.sub }}>
          <Legend color="#A5B4FC" label="Started" />
          <Legend color={C.primary} label="Completed" />
          <Legend color={C.green} label="AI-scored" />
        </div>
      </div>
      {!hasData ? (
        <EmptyState icon="📊" title="No interviews in this period" hint="Started, completed and AI-scored interviews will chart here over the last 14 days." />
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 130 }}>
            {list.map((d, i) => {
              const sH = ((d.started || 0) / max) * 110;
              const cH = ((d.completed || 0) / max) * 110;
              const scH = ((d.scored || 0) / max) * 110;
              return (
                <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 1, height: 110 }}>
                    <motion.div initial={{ height: 0 }} animate={{ height: sH }} transition={{ delay: (delay || 0) + 0.2 + i * 0.02, duration: 0.5 }} style={{ width: 5, background: "#A5B4FC", borderRadius: 1 }} title={`Started: ${d.started}`} />
                    <motion.div initial={{ height: 0 }} animate={{ height: cH }} transition={{ delay: (delay || 0) + 0.25 + i * 0.02, duration: 0.5 }} style={{ width: 5, background: C.primary, borderRadius: 1 }} title={`Completed: ${d.completed}`} />
                    <motion.div initial={{ height: 0 }} animate={{ height: scH }} transition={{ delay: (delay || 0) + 0.3 + i * 0.02, duration: 0.5 }} style={{ width: 5, background: C.green, borderRadius: 1 }} title={`Scored: ${d.scored}`} />
                  </div>
                  <span style={{ fontSize: 9, color: C.hush }}>{(d.day || "")[0]}</span>
                </div>
              );
            })}
          </div>
          {backlogHours > 4 && (
            <div style={{ marginTop: 10, padding: "6px 10px", background: `${C.gold}12`, border: `1px solid ${C.gold}33`, borderRadius: 6, fontSize: 11, color: C.gold }}>
              ⚠ AI scoring backlog: avg {backlogHours}h behind completed
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

// §9 Jobs Health
const th = { padding: "8px 6px", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4 };
const thCenter = { ...th, textAlign: "center" };
const td = { padding: "8px 6px", color: C.ink, fontWeight: 600 };
const tdCenter = { ...td, textAlign: "center", color: C.sub, fontWeight: 500 };

const JobsHealthTable = ({ rows, total, navigate, delay }) => {
  const list = Array.isArray(rows) ? rows : [];
  return (
    <motion.div {...reveal(delay)} className="card-glass" style={glassPad()}>
      <div style={cardHead}>
        <h3 style={h3}>Jobs health</h3>
        {total > 0 && (
          <button style={linkBtn} onClick={() => navigate("/admin/jobs")}>View all {total} jobs →</button>
        )}
      </div>
      {isEmptyArr(list) ? (
        <EmptyState icon="📋" title="No active jobs" hint="Create a job to start tracking applicants, interviews and time-to-fill." cta="Create a job" onCta={() => navigate("/admin/jobs")} />
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ textAlign: "left", color: C.hush, fontWeight: 600 }}>
                <th style={th}>Job</th>
                <th style={thCenter}>Open</th>
                <th style={thCenter}>Applied</th>
                <th style={thCenter}>Interv.</th>
                <th style={thCenter}>Scored</th>
                <th style={thCenter}>Shortl.</th>
                <th style={thCenter}>TTF</th>
                <th style={thCenter}>Owner</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.jobId || r.job} style={{ borderTop: `1px solid ${C.hairline}` }}>
                  <td style={td}>{r.job}</td>
                  <td style={tdCenter} className="font-kpi">{r.open}</td>
                  <td style={tdCenter} className="font-kpi">{r.applied}</td>
                  <td style={tdCenter} className="font-kpi">{r.interviewed}</td>
                  <td style={tdCenter} className="font-kpi">{r.scored}</td>
                  <td style={tdCenter} className="font-kpi">{r.shortlisted}</td>
                  <td style={{ ...tdCenter, color: r.warn ? C.red : C.sub, fontWeight: r.warn ? 700 : 500 }}>
                    {r.warn && "⚠ "}
                    {r.ttf}d
                  </td>
                  <td style={tdCenter}>
                    <span style={{ display: "inline-flex", width: 22, height: 22, borderRadius: 11, background: C.tint, color: C.primary, alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{r.owner}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
};

// §10 Sourcing Mix
const SourcingMix = ({ rows, delay }) => {
  const list = Array.isArray(rows) ? rows : [];
  const scored = list.filter((r) => typeof r.avgScore === "number");
  const top = scored.length ? [...scored].sort((a, b) => b.avgScore - a.avgScore)[0] : null;
  return (
    <motion.div {...reveal(delay)} className="card-glass" style={glassPad()}>
      <div style={cardHead}>
        <h3 style={h3}>Sourcing mix</h3>
      </div>
      {isEmptyArr(list) ? (
        <EmptyState icon="🧭" title="No sourcing data yet" hint="Once candidates come in via resume upload, job boards or referrals, the channel mix shows here." />
      ) : (
        <>
          <div style={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden", marginBottom: 12 }}>
            {list.map((r) => (
              <div key={r.source} style={{ width: `${r.pct}%`, background: r.color }} title={`${r.source}: ${r.pct}%`} />
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {list.map((r) => (
              <div key={r.source} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: r.color, flexShrink: 0 }} />
                <span style={{ flex: 1, color: C.sub }}>{r.source}</span>
                <span className="font-kpi" style={{ color: C.ink, fontWeight: 700 }}>{r.pct}%</span>
              </div>
            ))}
          </div>
          {top && (
            <div style={{ marginTop: 12, padding: "8px 10px", background: C.surface, borderRadius: 6, fontSize: 11, color: C.sub }}>
              Top performing: <strong style={{ color: C.ink }}>{top.source}</strong> ({top.avgScore} avg)
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

// §11 Activity Feed
const ActivityFeed = ({ items, total, navigate, delay }) => {
  const list = Array.isArray(items) ? items : [];
  return (
    <motion.div {...reveal(delay)} className="card-glass" style={glassPad()}>
      <div style={cardHead}>
        <h3 style={h3}>Activity</h3>
        {!isEmptyArr(list) && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="live-dot" />
            <button style={linkBtn} onClick={() => navigate("/admin/audit")}>Show more ({total} today) →</button>
          </div>
        )}
      </div>
      {isEmptyArr(list) ? (
        <EmptyState icon="📭" title="No activity yet" hint="Notes, stage moves and system events (AI scoring, emails) will stream here as they happen." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {list.map((it, i) => (
            <motion.div
              key={it.id || i}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: (delay || 0) + 0.15 + i * 0.05 }}
              style={{
                display: "grid",
                gridTemplateColumns: "40px 1fr",
                gap: 8,
                padding: "6px 0",
                fontSize: 12,
                borderBottom: i < list.length - 1 ? `1px solid ${C.hairline}` : "none",
              }}
            >
              <span style={{ color: C.hush, fontSize: 10, fontWeight: 600, alignSelf: "center", fontFamily: "'JetBrains Mono', monospace" }}>{it.time}</span>
              <span style={{ color: C.sub }}>
                <strong style={{ color: it.system ? C.hush : C.ink, fontWeight: 700 }}>{it.system ? "•" : it.actor}</strong>{" "}
                {it.text}
              </span>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

// §12 System Health
const SystemHealth = ({ rows, delay }) => {
  const list = Array.isArray(rows) ? rows : [];
  const dot = (s) => {
    if (s === "ok") return { label: "✓ OK", color: C.green };
    if (s === "warn") return { label: "⚠ degraded", color: C.gold };
    return { label: "✗ down", color: C.red };
  };
  return (
    <motion.div {...reveal(delay)} className="card-glass" style={glassPad()}>
      <div style={cardHead}>
        <h3 style={h3}>System health</h3>
      </div>
      {isEmptyArr(list) ? (
        <EmptyState icon="🩺" title="No system metrics available" hint="AI scoring, email/WhatsApp delivery and integration sync status will report here." />
      ) : (
        <div>
          {list.map((r, i) => {
            const d = dot(r.status);
            return (
              <div
                key={r.key || r.row}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  gap: 12,
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: i < list.length - 1 ? `1px solid ${C.hairline}` : "none",
                  fontSize: 12,
                }}
              >
                <span style={{ color: C.sub }}>{r.row}</span>
                <span className="font-kpi" style={{ color: C.ink, fontWeight: 600 }}>{r.value}</span>
                <span style={{ color: d.color, fontWeight: 700, minWidth: 80, textAlign: "right" }}>{d.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

/* ============================================================================
 * LOADING / ERROR SCAFFOLDS
 * ============================================================================ */
const LoadingSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
    <Shimmer h={34} w="60%" r={8} />
    <Shimmer h={40} w="100%" r={999} />
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <SkeletonCard key={i} lines={2} height={104} />
      ))}
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
      <SkeletonCard lines={6} />
      <SkeletonCard lines={4} />
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <SkeletonCard lines={5} />
      <SkeletonCard lines={5} />
    </div>
  </div>
);

const ErrorState = ({ message, onRetry }) => (
  <div className="card-glass" style={{ ...glassPad("48px 16px") }}>
    <EmptyState icon="⚠️" title="Couldn’t load your dashboard" hint={message || "Something went wrong fetching analytics. Please try again."} cta="Retry" onCta={onRetry} />
  </div>
);

/* ============================================================================
 * PAGE
 * ============================================================================ */
const DashboardV2 = () => {
  const navigate = useNavigate();
  // Per the integration doc, the server cannot reliably link a request to a user
  // record, so keep using localStorage for the greeting name; only orgName is
  // overridden from header.orgName once the aggregate loads.
  const userName = localStorage.getItem("currentUser") || "Admin";

  const [period, setPeriod] = useState("30d");
  const [reloadKey, setReloadKey] = useState(0); // bump to force an aggregate refetch (retry)
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [errMsg, setErrMsg] = useState("");
  const [dateStr, setDateStr] = useState("");

  // Live-status widgets refreshed independently (~60s) via their own endpoints;
  // they fall back to the aggregate payload until the first poll lands.
  const [polledHealth, setPolledHealth] = useState(null);
  const [polledActivity, setPolledActivity] = useState(null);

  useEffect(() => {
    setDateStr(new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" }));
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const loadAggregate = useCallback(
    (signal) => {
      setStatus("loading");
      return getDashboard(period, signal ? { signal } : {})
        .then((r) => {
          setData(r.data || {});
          setStatus("ok");
        })
        .catch((e) => {
          // Aborted requests (period change / unmount) are swallowed by the
          // caller's `cancelled` guard; only real failures reach the UI.
          throw e;
        });
    },
    // reloadKey is included so the Retry button can force a refetch for the same period.
    [period, reloadKey],
  );

  // Aggregate fetch on mount + whenever the period changes.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    loadAggregate(controller.signal).catch((e) => {
      if (cancelled) return;
      setErrMsg(e?.message || "Failed to load dashboard");
      setStatus("error");
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [loadAggregate]);

  // Independent ~60s polling for System Health + Activity. Reset on period
  // change so a stale period's data never bleeds across.
  useEffect(() => {
    let cancelled = false;
    setPolledHealth(null);
    setPolledActivity(null);

    const poll = () => {
      getSystemHealth(period)
        .then((r) => {
          if (cancelled) return;
          setPolledHealth(Array.isArray(r.data?.rows) ? r.data.rows : null);
        })
        .catch(() => {});
      getActivity(period)
        .then((r) => {
          if (!cancelled) setPolledActivity(r.data || null);
        })
        .catch(() => {});
    };

    const id = setInterval(poll, 60000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [period]);

  const orgName =
    data?.header?.orgName || localStorage.getItem("organizationName") || "Your organization";

  // Resolve widget payloads from the aggregate (inner shapes per the doc),
  // preferring freshly-polled data where available.
  const topCandidates = data?.topCandidates || {};
  const throughput = data?.interviewThroughput || {};
  const jobsHealth = data?.jobsHealth || {};
  const activity = polledActivity || data?.activityFeed || {};
  const systemHealthRows = polledHealth ?? data?.systemHealth ?? [];
  const kpiTiles = !isEmptyArr(data?.kpiTiles)
    ? data.kpiTiles
    : DEFAULT_KPIS.map((k) => ({ ...k, value: null }));

  const PeriodToggle = (
    <div style={{ display: "flex", gap: 6 }}>
      {PERIOD_OPTS.map((p) => {
        const active = p.value === period;
        return (
          <button
            key={p.value}
            onClick={() => setPeriod(p.value)}
            style={{
              padding: "5px 12px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 600,
              border: `1px solid ${active ? C.primary : C.border}`,
              background: active ? C.primary : "rgba(255,255,255,0.6)",
              color: active ? "white" : C.sub,
              cursor: "pointer",
              fontFamily: "inherit",
              transition: "all 0.2s",
            }}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{ display: "flex", flexDirection: "column", gap: 16, padding: 24, minWidth: 0, fontFamily: "'Inter', sans-serif" }}
    >
      {/* Component-scoped shimmer keyframes for skeleton loaders. */}
      <style>{`
        @keyframes dv2-pulse { 0% { opacity: 1; } 50% { opacity: 0.45; } 100% { opacity: 1; } }
        .dv2-shimmer { background: ${C.surface}; animation: dv2-pulse 1.2s ease-in-out infinite; }
      `}</style>

      {/* Header — Command Center */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16 }}>
        <div>
          <motion.h1
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            style={{ fontSize: 26, fontWeight: 800, margin: 0, letterSpacing: "-0.02em", fontFamily: "'Space Grotesk', sans-serif", color: C.ink }}
          >
            {greeting()}, {userName}{" "}
            <span style={{ fontWeight: 400, color: C.hush }}>—</span>{" "}
            <span style={{ background: "linear-gradient(135deg, #7B5AC4, #B865A8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Command Center</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            style={{ fontSize: 13, color: C.sub, margin: "4px 0 0", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
          >
            <span>{orgName}</span>
            <span style={{ background: "linear-gradient(135deg, #4FC5BF, #7B5AC4, #D946A8)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontWeight: 700 }}>AI</span>
            <span style={{ width: 3, height: 3, borderRadius: "50%", background: C.hush }} />
            <span>{dateStr || "…"}</span>
            {data?.scope === "own" && (
              <>
                <span style={{ width: 3, height: 3, borderRadius: "50%", background: C.hush }} />
                <span style={{ fontWeight: 600 }}>Your jobs</span>
              </>
            )}
          </motion.p>
        </div>
        {PeriodToggle}
      </div>

      {/* Onboarding: sample-data notice + setup checklist (each auto-hides). */}
      <SampleDataBanner />
      <SetupChecklist />

      {/* A scoped recruiter who owns nothing sees all-zero widgets. Say why,
          rather than letting it read as "the whole company has no data". */}
      {data?.scope === "own" && (data?.kpiTiles || []).every((t) => !t?.value) && (
        <div
          style={{
            margin: "0 0 16px", padding: "12px 14px", borderRadius: 10, fontSize: 13,
            background: "rgba(180,130,20,0.08)", color: "#8A6100",
            border: "1px solid rgba(180,130,20,0.2)",
          }}
        >
          This dashboard shows only jobs you own. Nothing is assigned to you yet —
          ask an admin to assign a job, or create one and it will appear here.
        </div>
      )}

      {status === "loading" && !data && <LoadingSkeleton />}

      {status === "error" && !data && (
        <ErrorState message={errMsg} onRetry={() => setReloadKey((k) => k + 1)} />
      )}

      {data && (
        <>
          {/* §3 Action Strip */}
          <ActionStrip chips={data.actionChips} navigate={navigate} />

          {/* §4 KPI Tiles */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, minWidth: 0 }}>
            {kpiTiles.map((t, i) => (
              <KpiTile key={t.key} tile={t} navigate={navigate} index={i} />
            ))}
          </div>

          {/* Row 3: Pipeline | Hiring goals */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
            <PipelineFunnel rows={data.pipelineFunnel} navigate={navigate} delay={0.15} />
            <HiringGoals goals={data.hiringGoals} delay={0.2} />
          </div>

          {/* Row 4: Top candidates | Interview throughput */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <TopCandidates rows={topCandidates.items} total={topCandidates.total} navigate={navigate} delay={0.25} />
            <InterviewThroughput data={throughput.days} backlogHours={throughput.aiBacklogHours} delay={0.3} />
          </div>

          {/* Row 5: Jobs health | Sourcing mix */}
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
            <JobsHealthTable rows={jobsHealth.rows} total={jobsHealth.total} navigate={navigate} delay={0.35} />
            <SourcingMix rows={data.sourcingMix} delay={0.4} />
          </div>

          {/* Row 6: Activity | System health */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <ActivityFeed items={activity.items} total={activity.totalToday} navigate={navigate} delay={0.45} />
            <SystemHealth rows={systemHealthRows} delay={0.5} />
          </div>

          {/* Quick Access footer */}
          <motion.div {...reveal(0.55)} className="card-glass" style={glassPad()}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div>
                <h3 style={{ ...h3, marginBottom: 4 }}>Quick Access</h3>
                <p style={{ fontSize: 11, color: C.hush, margin: 0 }}>Jump to your hiring pipeline</p>
              </div>
              <button
                onClick={() => navigate("/admin/pipeline")}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 8, background: `${C.primary}1A`, border: `1px solid ${C.primary}4D`, color: C.primary, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
              >
                Track Pipeline <ArrowRight size={14} />
              </button>
            </div>
          </motion.div>
        </>
      )}
    </motion.div>
  );
};

export default DashboardV2;
